import React, {
    useState, useContext, useCallback, useMemo, useEffect
} from 'react';
import _ from 'lodash';
import { ModalNextProvider } from '@jutro/components';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { TranslatorContext } from '@jutro/locale';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { PolicyChange, messages as commonMessage } from 'gw-capability-policychange-common-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { EntityUtil } from 'gw-portals-util-js';
import { messages as platformMessages } from 'gw-platform-translations';
import metadata from './DriversPage.metadata.json5';
import styles from '../../BAPolicyChange.module.scss';
import messages from './DriversPage.messages';
import { GatewayEndorsementService } from 'gw-capability-gateway-policychange';

const drivers = 'lobData.businessAuto.coverables.drivers';
const vehicles = 'lobData.businessAuto.coverables.vehicles';
function DriversPage(props) {
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [driverPath, setDriverPath] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState({});
    const { wizardData, updateWizardData, wizardSnapshot } = props;
    const { submissionVM } = wizardData;
    const [isVmIntitialized, updateIsVmInitialized] = useState(false);
    const { EndorsementService } = useDependencies('EndorsementService');
    const [isReplace, setIsReplace] = useState(false);
    const [replaceData, setReplaceData] = useState({});
    const [existingDrivers, setExistingDrivers] = useState([]);
    const [previousData, setPreviousData] = useState({});
    const {
        onValidate,
        initialValidation,
        isComponentValid,
        disregardFieldValidation
    } = useValidation('BAPolicyChangeDriverPage');
    const { authHeader } = useAuthentication();

    useEffect(() => {
        const _existingDrivers = submissionVM.baseData.account.accountContacts.value.map((c) => {
            c['id'] = c.publicID;
            return c;
        });
        const availableDrivers = [{ id: "placeholder", displayName: "Create New Driver" }].concat(_existingDrivers);
        setExistingDrivers(availableDrivers);
    },[submissionVM, setExistingDrivers])

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData, wizardData]
    );

    const showLicenseGroupModal = useCallback(
        (driverGroup) => {
            return ModalNextProvider.showAlert({
                title: messages.licenseNotUnique,
                message: translator(messages.licenceMatchMsg, {
                    driver: driverGroup[0].displayName || driverGroup[0].displayName
                }),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: platformMessages.ok
            }).catch(_.noop);
        },
        [translator]
    );

    const uniqueLicenceNumber = useCallback(() => {
        let licenseCheckError = false;
        const driverList = _.get(submissionVM.value, 'lobData.businessAuto.coverables.drivers');
        const groupedDrivers = _.groupBy(driverList, (driver) => {
            return driver.licenseNumber;
        });
        Object.keys(groupedDrivers).forEach((driver) => {
            if (groupedDrivers[driver].length > 1) {
                showLicenseGroupModal(groupedDrivers[driver]);
                licenseCheckError = true;
                return false;
            }
            return true;
        });
        return licenseCheckError;
    }, [showLicenseGroupModal, submissionVM.value]);

    const onNext = useCallback(async () => {
        if (!uniqueLicenceNumber()) {
            if (!submissionVM.value.validate()) {
                updateWizardData(wizardData);
                return false;
            }
            submissionVM.value.beforeSave();
            const policyChangeResponse = await EndorsementService.saveEndorsement(
                [submissionVM.value],
                authHeader
            );
            submissionVM.value = new PolicyChange(policyChangeResponse);
            return wizardData;
        }
        return false;
    }, [
        authHeader,
        EndorsementService,
        submissionVM.value,
        wizardData,
        updateWizardData,
        uniqueLicenceNumber
    ]);

    const deleteDriver = useCallback(
        (index) => {
            submissionVM.lobData.businessAuto.coverables.drivers.value.splice(index, 1)
            disregardFieldValidation(`driver${index}`);
            wizardData.submissionVM = submissionVM;
            updateWizardData(wizardData);
        },
        [disregardFieldValidation, submissionVM.value, updateWizardData, wizardData]
    );

    const hideDriverDetails = useCallback(
        (driversList, driver, index, isCancel) => {
            if (isEdit && !isCancel) {
                setDriverPath('');
                setIsEdit(false);
                return
            }
            if (isReplace) {
                const prevValue = _.get(
                    wizardSnapshot,
                    `submissionVM.lobData.businessAuto.coverables.drivers.children.${index}`
                )

                if(!_.isUndefined(prevValue)){
                    _.set(
                        submissionVM,
                        `lobData.businessAuto.coverables.drivers.children[${index}].value`,
                        prevValue.value
                    ); 
                } else {
                    _.set(
                        submissionVM,
                        `lobData.businessAuto.coverables.drivers.children[${index}].value`,
                        previousData
                    ); 
                }

                submissionVM.lobData.businessAuto.coverables.vehicleDrivers.children.forEach((d) => {
                    if (d.driverID.value == replaceData.current) {
                        d.driverID.value = replaceData.previous,
                            d.driverTempID.value = undefined
                    }
                });
                disregardFieldValidation(`driver${index}`);
                setReplaceData({});
                setDriverPath('');
                setIsReplace(false);
                updateWizardData(wizardData);
                return
            }
            if (_.get(driversList.value[index], 'tempID')) {
                deleteDriver(index);
                setIsEdit(false);
            }
            else if (_.get(driversList.value[index], 'fixedId')) {
                const prevValue = _.get(
                    wizardSnapshot,
                    'submissionVM.lobData.businessAuto.coverables.drivers.children'
                ).find(
                    (driver) => driver.value.fixedId === _.get(driversList.value[index], 'fixedId')
                );
                _.set(
                    submissionVM,
                    `lobData.businessAuto.coverables.drivers.children[${index}].value`,
                    prevValue.value
                );
                setIsEdit(false);
                updateWizardData(wizardData);
            }
            disregardFieldValidation(`driver${index}`);
            setDriverPath('');
        },
        [
            deleteDriver,
            disregardFieldValidation,
            isComponentValid,
            submissionVM,
            updateWizardData,
            wizardData,
            wizardSnapshot
        ]
    );

    const saveDriverDetails = () => {
        setIsEdit(false);
        setIsReplace(false);
        setDriverPath('');
    }

    const showDriverDetails = useCallback((driver, index) => {
        driver.isValidationRequired = true;
        setIsEdit(true);
        setDriverPath(`lobData.businessAuto.coverables.drivers.children.${index}`);
        setSelectedDriver(driver);
    }, []);

    const removeDriver = useCallback(
        (index) => {
            ModalNextProvider.showConfirm({
                title: messages.removeDriverTitle,
                message: messages.removeDriverDescription,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }
                    submissionVM.lobData.businessAuto.coverables.drivers.value.splice(index, 1)
                    disregardFieldValidation(`driver${index}`);
                    wizardData.submissionVM = submissionVM;
                    updateWizardData(wizardData);
                    return true;
                }, _.noop
            );
        },
        [disregardFieldValidation, submissionVM, updateWizardData]
    );

    const updateSubmission = (_submissionVM) => {
        const newSubmissionVMClone = viewModelService.clone(_submissionVM);
        wizardData.submissionVM = newSubmissionVMClone;
        updateWizardData(wizardData);
    }

    const replaceDriver = (index) => {
        setIsReplace(true);
        const prevDriver = submissionVM.lobData.businessAuto.coverables.drivers.value[index];
        setPreviousData(prevDriver);
        const driverObj = {
            accidents: '0',
            violations: '0',
            isPolicyHolder: false,
            tempID: EntityUtil.nextId(),
            isValidationRequired: true,
            licenseState: 'ON'
        };
        const { _xCenter, _dtoName } = submissionVM.lobData.businessAuto.coverables.drivers;
        const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);
        driverVM.person = {};
        driverVM.driverConvictionDetails = [];
        submissionVM.lobData.businessAuto.coverables.drivers.value[index] = driverVM.value;
        submissionVM.lobData.businessAuto.coverables.vehicleDrivers.children.forEach((d) => {
            if (d.driverID.value == prevDriver.fixedId) {
                d.driverID.value = undefined,
                    d.driverTempID.value = driverVM.tempID.value
            }
        });
        setReplaceData({ previous: prevDriver.fixedId, current: driverVM.tempID.value });
        setDriverPath(`lobData.businessAuto.coverables.drivers.children.${index}`);
        setSelectedDriver(driverVM);
        wizardData.submissionVM = submissionVM;
        updateWizardData(wizardData);
    }

    const driverOverrides = useCallback(() => {
        const availableDriverPath = 'lobData.businessAuto.coverables.drivers.children';
        const driversList = _.get(submissionVM, drivers, []);
        const vehicleList = _.get(submissionVM, vehicles, []);
        let overrides = [];
        if (!_.isEmpty(driversList)) {
            overrides = driversList.value.map((driver, index) => {
                const driverName = driver.person.displayName || `${driver.person.firstName || ''} ${driver.person.lastName || ''}`;
                const vehicleInfo = vehicleList.value.map((vehicle) => {
                    return {
                        code: `${driver.fixedId || driver.tempID}_${vehicle.fixedId.toString()}`,
                        name: vehicle.displayName
                    };
                });
                return {
                    [`baDriverContainer${index}`]: {
                        visible: `${availableDriverPath}.${index}` === driverPath
                    },
                    [`driver${index}`]: {
                        submissionVM: submissionVM,
                        updateWizardData: updateSubmission,
                        showAccountHolderToggle: false
                    },
                    [`baDriverSectionNameId${index}`]: {
                        value: driverName.trim() || translator(messages.baNewDriver),
                        className: styles.driverNameLabel
                    },
                    [`baDriverSectionTrashId${index}`]: {
                        disabled:
                            driversList.length === 1
                            || (`${availableDriverPath}.${index}` !== driverPath && !isComponentValid) || isEdit,
                        visible: `${availableDriverPath}.${index}` != driverPath,
                        onClick: () => {
                            removeDriver(index);
                        }
                    },
                    [`baDriverSectionEditId${index}`]: {
                        disabled:
                            `${availableDriverPath}.${index}` !== driverPath && !isComponentValid || isEdit,
                        visible: `${availableDriverPath}.${index}` !== driverPath,
                        onClick: () => {
                            showDriverDetails(driver, index);
                        }
                    },
                    [`cancelBtnId${index}`]: {
                        onClick: () => {
                            hideDriverDetails(driversList, driver, index, true);
                        }
                    },
                    [`saveBtnId${index}`]: {
                        disabled: `${availableDriverPath}.${index}` == driverPath && !isComponentValid,
                        onClick: () => {
                            saveDriverDetails(driversList, driver, index);
                        }
                    },
                    [`baDriverSubstitute${index}`]: {
                        disabled: `${availableDriverPath}[${index}].value` == driverPath && !isComponentValid || isEdit || isReplace,
                        onClick: () => {
                            replaceDriver(index);
                        }
                    }
                };
            });
        }
        return Object.assign({}, ...overrides);
    }, [
        submissionVM,
        driverPath,
        translator,
        isComponentValid,
        removeDriver,
        showDriverDetails,
        hideDriverDetails
    ]);

    const setDefaultNewEntry = useCallback((driver) => {
        let driverObj = {
            accidents: '0',
            violations: '0',
            isPolicyHolder: false,
            tempID: EntityUtil.nextId(),
            isValidationRequired: true,
            licenseState: 'ON',
        };

        if (driver) {
            driverObj = Object.assign(driverObj, driver)
        }

        const { _xCenter, _dtoName } = submissionVM.lobData.businessAuto.coverables.drivers;
        const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);
        driverVM.person = driver ? driverVM.person : {};
        driverVM.driverConvictionDetails = [];
        submissionVM.lobData.businessAuto.coverables.drivers.pushElement(driverVM);
        setSelectedDriver(driverVM);
    }, [submissionVM.value.lobData.businessAuto.coverables, setSelectedDriver]);

    const onAddNew = useCallback((driver) => {
        setDefaultNewEntry(driver);
        const newVehicleIndex = submissionVM.lobData.businessAuto.coverables.drivers.length - 1;
        setDriverPath(`lobData.businessAuto.coverables.drivers.children.${newVehicleIndex}`);
        _.set(
            submissionVM.value,
            `lobData.businessAuto.coverables.drivers[${newVehicleIndex}].accidents`,
            '0'
        );
        _.set(
            submissionVM.value,
            `lobData.businessAuto.coverables.drivers[${newVehicleIndex}].violations`,
            '0'
        );
        updateWizardData(wizardData);
    }, [setDefaultNewEntry, submissionVM, updateWizardData, wizardData]);

    const addExistingDriver = async(value) => {  
        const {jobID} = submissionVM.value;                
        if (value == 'placeholder') {
            onAddNew();
            setIsEdit(true);
            return
        }
        const driver = submissionVM.baseData.account.accountContacts.value.filter((c) => {
            return c.publicID == value
        })[0];

        if(driver){
            let licenseClassificationData = await GatewayEndorsementService.updateLicenseClassDetails(
                    jobID,
                    driver.publicID,
                    authHeader
                );
            const _driver = {
                sameAsAccountHolder: driver.accountHolder,
                dateOfBirth: driver.dateOfBirth,
                gender: driver.gender,
                licenseNumber: driver.licenseNumber,
                licenseState: driver.licenseState,
                maritalStatus: driver.maritalStatus,
                licenseStatus: driver.licenseStatus,
                firstLicenseDate: driver.firstLicenseDate,
                occupation: driver.occupation,
                typeOfEmployment_EY: driver.typeOfEmployment_EY,
                licenseClassificationRecords: licenseClassificationData ? licenseClassificationData : driver.licenseClassificationRecords,
                person: {
                    displayName: driver.displayName,
                    firstName: driver.firstName,
                    lastName: driver.lastName,
                    primaryPhoneType: driver.primaryPhoneType || undefined,
                    workNumber: driver.workNumber || undefined,
                    homeNumber: driver.homeNumber || undefined,
                    publicID: driver.publicID,
                }
            }
            onAddNew(_driver);
            setIsEdit(true);
        }
        
    }


    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onAddDriverClick: onAddNew,
            onValidate: onValidate
        }
    };

    const overrideProps = {
        '@field': {
            showOptional: true
        },
        addDriver: {
            disabled: !isComponentValid
        },
        baPolicyChangeAddDriverButtonContainer:{
            visible: !(!isComponentValid || isEdit || isReplace),
        },
        addNewDriverContainer:{
            visible: !(!isComponentValid || isEdit || isReplace),
        },
        driverContacts: {             
            availableValues: existingDrivers,
            onValueChange: addExistingDriver,
            searchable: true
        },
        ...driverOverrides()
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [submissionVM, overrideProps]
    );

    if (!isVmIntitialized) {
        updateWizardData(wizardData);
        updateIsVmInitialized(true);
        return null;
    }

    return (
        <WizardPage
            onNext={onNext}
            disableNext={!isComponentValid || isEdit || isReplace}
            skipWhen={initialValidation}
            cancelLabel={(appConfig.persona === 'policyholder' ? commonMessage.cancelAllChanges : commonMessage.cancel)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onValueChange={writeValue}
                callbackMap={resolvers.resolveCallbackMap}
                resolveValue={readValue}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

DriversPage.propTypes = wizardProps;
export default DriversPage;
